import { a, useSpring, easings } from "@react-spring/three";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSocket } from "../../contexts/SocketContext";
import { ObjectMap, ThreeEvent, useFrame, useLoader, useThree } from "@react-three/fiber";
import { GLTF, GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { BlockTradeEvent, Card, FlipAllCardsEvent, GameStartedReturn, socketKeys, TradeCardsEvent, UnknownRoundEvent } from "../../types/SharedTypes";
import { socket } from "../../socket";
import * as THREE from "three";
import { Html } from "@react-three/drei";
import { createSafeInvalidate, getPlayingCardUI } from "../../tools/helpers";
import { useStateRef } from "../../hooks/useStateRef";
import SoundManager from "../../tools/SoundManager";
import { PointerIcon } from "../../assets/icons";

type Coords = [number, number, number];

const TABLE_POSITION: Coords = [0,4.2,0];

type TableCardData = {
  userId: string;
  playerPosition: number;
  position: Coords[];
  rotation: Coords[];
  action?: string[];
}

const getMidArrays = (posA: Coords, posB: Coords, rotA: Coords, rotB: Coords) => {
  const position: Coords = [
    (posA[0] + posB[0]) / 2,
    (posA[1] + posB[1]) / 2,
    (posA[2] + posB[2]) / 2,
  ]

  const rotation: Coords = [
    (rotA[0] + rotB[0]) / 2,
    (rotA[1] + rotB[1]) / 2,
    (rotA[2] + rotB[2]) / 2,
  ]

  return { position, rotation };
}

export const getPositionAroundTable = (
  playerPosition: number,
  totalPlayers: number,
  radius = 2,
  y = 2.2,
  rotMod = 0
): {position: Coords, rotation: number} => {
  if (playerPosition < 0 || playerPosition > totalPlayers) throw new Error("Invalid position number");

  // Evenly spaced angle step
  const angleStep = (2 * Math.PI) / totalPlayers;

  // Place current player (index 0) at angle = 0 (facing +Z direction)
  const angle = angleStep * playerPosition * -1;

  const x = radius * Math.sin(angle);
  const z = radius * Math.cos(angle);

  // Rotation to face the center
  const rotY = Math.atan2(-x, -z) + rotMod;

  return {position: [x, y, z], rotation: rotY};
}

export const getRelativeTablePosition = (
  player: number,
  currentPlayer: number | undefined,
  totalPlayers: number,
  uninitFallback: number
): number => {
  if (!currentPlayer || totalPlayers === 0) return 0;

  if (player === -1) {
    console.log('returning uninit');
    return uninitFallback;
  }
  const relPos = (player - currentPlayer + totalPlayers) % totalPlayers;
  return relPos;
}

const getRelativeDealPosition = (
  playerPosition: number,
  dealerPosition: number | undefined,
  totalPlayers: number
) => {
  return totalPlayers - ((dealerPosition || 1) - playerPosition + totalPlayers) % totalPlayers;
}

const getPositionRelativeTable = (position: Coords): Coords => {
  return [position[0] + TABLE_POSITION[0], position[1] + TABLE_POSITION[1], position[2] + TABLE_POSITION[2]];
}

const shortestRotation = (from: number, to: number) => {
  const angle = to-from;
  let diff = Math.atan2(Math.sin(angle), Math.cos(angle));
  return from + diff;
}

function normalizeCardRotation(from: number, to: number): number {
  const PI = Math.PI;
  const TWO_PI = 2 * PI;

  // Step 1: normalize difference
  let diff = to - from;

  // Wrap into [-π, π]
  diff = ((diff + PI) % TWO_PI + TWO_PI) % TWO_PI - PI;

  // Step 2: cards are symmetric top/bottom → clamp to [-π/2, π/2]
  if (diff < -PI / 2) diff += PI;
  else if (diff > PI / 2) diff -= PI;

  // Step 3: return adjusted "to"
  return from + diff;
}

export const TableObject = () => {
  const obj = useLoader(GLTFLoader, '/3d/table/scene.gltf');

  return (
    <primitive rotation={[0, -0.27, 0]} scale={[1.7,1.7,1.7]} position={TABLE_POSITION} object={obj.scene} />
  )
}

export const Table = () => {

  const {players, currentDealer, currentPlayer, allPlayerInfoRef, game, currentPlayerRef} = useSocket();

  const [deckPosition, setDeckPosition, deckPositionRef] = useStateRef<{position: Coords; rotation: number} | null>(null);
  const [cards, setCards] = useState<TableCardData[]>([]);

  //game start, deck is in right place and cards are dealt
  //dealer does action and flip-all-cards happens
  //cards are pulled back into deck
  //next round starts
  //deck is moved then cards are dealt

  const getCardsData = (deckPosition: {position: Coords; rotation: number}) => {
    if (!deckPosition) return [];

    console.log('init cards');

    const cards: TableCardData[] = [];
    for (const player of allPlayerInfoRef.current.players) {
      if (player.lives < 1) continue;
      cards.push({
        userId: player.userId,
        playerPosition: player.position,
        position: [deckPosition.position], //deck position
        rotation: [[0, deckPosition.rotation + 1.571, 1.571]]
      })
    }
    return cards;
  }

  const setDeckPositionAndCards = () => {
    if (!currentDealer || !currentPlayer) return;

    console.log('set deck & cards', currentDealer.position, currentPlayer.position, players.length);

    const relativePosition = getRelativeTablePosition(currentDealer.position || 0, currentPlayer.position, players.length, 0);
    const _deckPosition = getPositionAroundTable(
      relativePosition,
      players.length,
      0.9,
      TABLE_POSITION[1] + 0.2
    );

    setDeckPosition(_deckPosition);

    const cards = getCardsData(_deckPosition);
    moveDeckAndDealCards(cards);
  }

  const moveDeckAndDealCards = (_cards: TableCardData[]) => {

    console.log('setting card deal positions');

    //first position is deck position, then dealt position
    const newCards = _cards.map((card, i) => {
      console.log(`card ${i}:`, card.playerPosition, currentPlayer?.position, currentPlayerRef.current?.position);
      const relativePosition = getRelativeTablePosition(card.playerPosition, currentPlayer?.position, players.length, 0);
      const position = getPositionAroundTable(relativePosition, players.length, 1.3, TABLE_POSITION[1] + 0.2);
      
      const finalRotationY = normalizeCardRotation(card.rotation[0][1], position.rotation + 1.571);
      const finalRotation: Coords = [0, finalRotationY, 1.571];

      console.log('deck pos', card.position[0]);
      console.log('new pos', [...position.position]);

      return ({
        ...card,
        position: [card.position[0], position.position],
        rotation: [card.rotation[0], finalRotation],
        action: ['move-deck', 'deal-out']
      });
    });

    console.log('NEW CARDS', {...newCards});
    setCards(newCards);
  }
  
  useEffect(() => {
    //set deck everytime the dealer changes or game resets
    console.log('trigged from pos change');
    setDeckPositionAndCards();
  }, [currentDealer?.position, !!game]);

  return (
    <>
      {deckPosition && 
        <TableDeck position={deckPosition.position} rotation={deckPosition.rotation} />
      }
      {cards.map((card) => (
        <TableCard key={card.userId} card={card} numberOfCards={cards.length} />
      ))}
      <TableObject />
    </>
  )
}

const dummyDeckCards = [0,0,0,0,0,0,0,0,0,0,0,0,0,0];

const TableDeck = ({position, rotation}: {position: Coords, rotation: number}) => {

  const {isCuttingDeck} = useSocket();

  const obj = useLoader(GLTFLoader, '/3d/cardtest/cardtest.gltf');
  const objRef = useRef<THREE.Mesh>(undefined);
  const modVector = useRef(new THREE.Vector3(0.196,0.015,-0.05));
  const cutUiPosition = useRef(new THREE.Vector3(position[0], 5.6, position[2]+2.5).add(modVector.current));

  const spring = useSpring({
    position: isCuttingDeck ? [position[0], 5.6, position[2]+2.5] as Coords : position,
    rotation: isCuttingDeck ? [0.15, rotation-0.5, 0.1] : [0, rotation, 0],
    config: { tension: 200, friction: 20, duration: 300}
  });

  useEffect(() => {
    cutUiPosition.current = new THREE.Vector3(position[0], 5.6, position[2]+2.5).add(modVector.current);
  }, [position]);

  return (
    <>
      <group position={cutUiPosition.current}>
        {isCuttingDeck &&
          <mesh>
            <Html
              position={[0, 0, 0]} // Offset UI above the model
              rotation={[0.15, -0.5, -0.1]}
              occlude={false}
              transform
              distanceFactor={0.3} // Controls UI size relative to distance
              zIndexRange={[0, 0]}
            >
              <div className="item-row">
                <div className="cut-indicator">
                  <div className="icon-display">
                    <PointerIcon />
                  </div>
                </div>
                <div className="item-list med-gap">
                  <p className="heading-medium white">Choose</p>
                  <p className="heading-medium white">Your</p>
                  <p className="heading-medium white">Card</p>
                </div>
              </div>
            </Html>
          </mesh>
        }
      </group>
      <a.group
        ref={objRef}
        position={spring.position}
        rotation={spring.rotation as unknown as Coords}
      >
        {dummyDeckCards.map((_, i) =>
          <DeckCard key={i} cardObj={obj} i={i} />
        )}
      </a.group>
    </>
  )
}

const DeckCard = ({cardObj, i}: {
  cardObj: GLTF & ObjectMap;
  i: number;
}) => {

  const {isCuttingDeck, makeGameChoice, setIsCuttingDeck} = useSocket();
  const {invalidate} = useThree();

  const [isHovered, setIsHovered] = useState(false);
  const objRef = useRef<THREE.Mesh>(null);

  const spring = useSpring({
    position: [isHovered ? -0.013 : 0, 0+(0.004*i), 0] as Coords,
    config: { tension: 200, friction: 20},
    onStart: () => invalidate(),
    onRest: () => invalidate(),
  });

  const handleMouseEnter = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    setIsHovered(true);
  }

  const handleCut = (e: ThreeEvent<PointerEvent>) => {
    if (!isCuttingDeck) return;
    e.stopPropagation();
    setIsHovered(false);
    SoundManager.play3DGameSound('card_cut', objRef.current);
    //1-position to invert it since 0 is at bottom
    makeGameChoice('cut', 1 - (i/dummyDeckCards.length));
    console.log('would cut at %', i/dummyDeckCards.length*100);
  }

  useEffect(() => {
    if (!isCuttingDeck) setIsHovered(false);
  }, [isCuttingDeck]);

  useFrame(() => {
    if (spring.position.isAnimating) invalidate();
  });

  const object = useMemo(() => cardObj.scene.clone(), [i]);

  return (
    <a.mesh
      ref={objRef}
      scale={[0.05,0.1,0.1]}
      rotation={[0, 1.571, 1.571]}
      position={spring.position}
      onPointerEnter={isCuttingDeck ? handleMouseEnter : undefined}
      onPointerLeave={isCuttingDeck ? () => setIsHovered(false) : undefined}
      onClick={isCuttingDeck ? handleCut : undefined}
    >
      <primitive object={object} />
    </a.mesh>
  )
}

const TableCard = ({card, numberOfCards}: {card: TableCardData, numberOfCards: number}) => {

  const {invalidate} = useThree();
  const safeInvalidate = createSafeInvalidate(invalidate);
  const obj = useLoader(GLTFLoader, '/3d/cardtest/cardtest.gltf');

  const {currentLosers, currentDealer, currentPlayerRef, allPlayerInfoRef} = useSocket();

  const cardRef = useRef<TableCardData>(card);
  const isAnimatingRef = useRef(false);
  const uiRef = useRef<THREE.Group>(null);
  const objRef = useRef<THREE.Mesh>(null);
  const exposedCardRef = useRef<Card>(null);

  const [spring2, api2] = useSpring({
    from: {
      rotation: [0,0,0] as Coords
    },
    onStart: () => {
      isAnimatingRef.current = true;
      safeInvalidate();
    },
    onRest: () => {
      isAnimatingRef.current = false;
      safeInvalidate();
    },
  }, []);
  
  const [spring, api] = useSpring({
    from: {
      position: [0,0,0] as Coords,
      rotation: [0,0,0] as Coords
    },
    onStart: () => {
      isAnimatingRef.current = true;
      safeInvalidate();
    },
    onRest: () => {
      isAnimatingRef.current = false;
      safeInvalidate();
    },
  }, []);

  useEffect(() => {
    //when a user initiates a swap, that user's card and the next card need to participate in an animation
    //calculate the middle point (considering the circle arc maybe)
    //then animate both cards to the same point, then back, appearing as if they swapped

    const flipCardAnimation = (playSound?: boolean) => {

      //final flip is a global sound from socket context
      if (playSound) SoundManager.play3DGameSound("card_flip", objRef.current);

      api2.start({
        from: {
          rotation: [0,0,0],
        },
        to: [{
          rotation: [0, Math.PI, 0],
          config: {duration: 400},
        }]
      })
    }

    const handleGameRoundEvent = (_data: UnknownRoundEvent) => {

      const card = cardRef.current;

      if (_data.event === 'trade-cards') {
        const data = _data as TradeCardsEvent;

        const isFrom = card.userId === data.data.from;
        const isTo = card.userId === data.data.to;

        if (!isFrom && !isTo) return;

        const otherPosition = allPlayerInfoRef.current.players.find(pl => pl.userId === data.data[isFrom ? 'to' : 'from'])?.position;
        const relativePosition = getRelativeTablePosition(otherPosition || 0, currentPlayerRef.current?.position, allPlayerInfoRef.current.players.length, -1);
        const position = getPositionAroundTable(relativePosition, allPlayerInfoRef.current.players.length, 1.3, TABLE_POSITION[1] + 0.2);
        
        const finalRotationY = normalizeCardRotation(card.rotation[0][1], position.rotation + 1.571);
        const finalRotation: Coords = [0, finalRotationY, 1.571];

        const calculated = {position: position.position, rotation: finalRotation};

        const fromCoords = isFrom ? {
          position: card.position[card.position.length - 1],
          rotation: card.rotation[card.rotation.length - 1]
        } : calculated;

        const toCoords = isTo ? {
          position: card.position[card.position.length - 1],
          rotation: card.rotation[card.rotation.length - 1]
        } : calculated;

        isFrom && console.log('FROM', card.playerPosition, fromCoords.position, toCoords.position);
        isTo && console.log('TO', card.playerPosition, toCoords.position, fromCoords.position);

        const mid = getMidArrays(fromCoords.position, toCoords.position, fromCoords.rotation, toCoords.rotation);

        //first new position, then existing position
        SoundManager.play3DGameSound('card_slide', objRef.current);
        api.start({
          from: {
            position: card.position[card.position.length - 1],
            rotation: card.rotation[card.rotation.length - 1],
          },
          to: [{
            position: mid.position,
            rotation: mid.rotation,
            config: {duration: 300, easing: easings.linear},
            delay: 0
          },{
            position: card.position[card.position.length - 1],
            rotation: card.rotation[card.rotation.length - 1],
            config: {duration: 300, easing: easings.linear},
            delay: 0
          }]
        })
      }
      else if (_data.event === "block-trade") {
        const data = _data as BlockTradeEvent;
        const toUser = data.data.to;

        if (card.userId !== toUser) return;        
        exposedCardRef.current = data.data.card;

        flipCardAnimation(true);
      }
      else if (_data.event === 'flip-all-cards') {
        const data = _data as FlipAllCardsEvent;

        if (spring2.rotation.get()[1] !== 0) return;

        const thisCard = data.data.players.players.find(pl => pl.userId === card.userId);
        exposedCardRef.current = thisCard?.card || null;

        flipCardAnimation();
      }
    }

    socket.on(socketKeys.toClient.game.roundEvent, handleGameRoundEvent);

    return () => {
      socket.off(socketKeys.toClient.game.roundEvent, handleGameRoundEvent);
    }
  }, []);

  useEffect(() => {

    //keep ref up to date
    cardRef.current = card;

    if (card.position.length !== card.rotation.length) {
      console.log('anim length mismatch');
      return;
    }

    exposedCardRef.current = null;
    isAnimatingRef.current = true;

    const relativePos = getRelativeDealPosition(card.playerPosition, currentDealer?.position, numberOfCards);

    const data = [];
    console.log('handling deal of card ', card.playerPosition);
    for (let i = 0; i < card.position.length; i++) {
      console.log('pushing', i, {...card});
      const isDeal = card?.action?.[i] === 'deal-out';
      data.push({
        position: card.position[i],
        rotation: card.rotation[i],
        config: {
          duration: 400 + (isDeal ? (relativePos-1) * 150 : 0),
          easing: isDeal ? easings.easeInOutSine : easings.linear
        },
        //delay seems to be causing our deal issues
        // delay: isDeal ? relativePos * 100 : 0,
        onStart: () => {
          console.log('starting deal', card.playerPosition, i);
          if (isDeal) SoundManager.play3DGameSound('card_deal', objRef.current);
        }
      })
    }

    const to = {to: [...data]};
    console.log('anim to FINAL', {...to});
    api.start(to);
    api2.start({to: [{rotation: [0,0,0], config: {duration: 100}}]});
    safeInvalidate();
  }, [card])

  useFrame(({ camera }) => {

    if (isAnimatingRef.current || spring.position.isAnimating || spring2.rotation.isAnimating) invalidate();

    //track ui to card
    if (uiRef.current && objRef.current) {
      uiRef.current.position.copy(objRef.current.position).add(new THREE.Vector3(0, 0.15, 0)); // Adjust height as needed
      uiRef.current.quaternion.copy(camera.quaternion);
    }
  });

  const object = useMemo(() => obj.scene.clone(), [card, spring, api]);

  return (
    <>
      <a.mesh
        ref={objRef}
        scale={[0.1, 0.1, 0.1]}
        position={spring.position}
        rotation={spring.rotation as unknown as Coords}
      >
        <a.mesh rotation={spring2.rotation as unknown as Coords}>
          <primitive object={object} />
        </a.mesh>
      </a.mesh>
      {exposedCardRef.current &&
        <group ref={uiRef}>
          <mesh>
            <Html
              position={[-0.1, 0.1, 0]} // Offset UI above the model
              occlude={false}
              distanceFactor={3} // Controls UI size relative to distance
              zIndexRange={[100, 0]}
            >
              <PlayingCardUI
                card={exposedCardRef.current}
                loser={currentLosers.some(f => f.userId === card.userId)}
              />
            </Html>
          </mesh>
        </group>
      }
    </>
  )
}

const PlayingCardUI = ({card, loser}: {card: Card, loser: boolean}) => {

  const {realRank, realSuit} = getPlayingCardUI(card);

  return (
    <div className="item-row small-gap tile bubble" data-padding-small={true} data-alert={loser}>
      <p className="heading-large weight-bold">{realRank}</p>
      <div className="icon-display large">
        {realSuit()}
      </div>
    </div>
  )
}